import BybAnimatedBorder from "@Components/common/byb-ds/byb-animated-border";
import BybConditionalWrapper from "@Components/common/byb-ds/byb-conditional-wrapper";
import HapticsButtonWrapper from "@Components/generic/haptics-button-wrapper";
import Icon from "@Components/generic/icon";
import { AppContext } from "@Context/common/general/context";
import { useScenarioRetryContext } from "@Context/game/activity/scenario-retry-context";
import { useSessionResultsStaticContext, useSessionResultsTabContext } from "@Context/game/activity/session-results-context";
import { GameModes } from "@Enums/index";
import { faRotateRight } from "@fortawesome/pro-solid-svg-icons";
import { hasBetterReplayDecision, type ReplayDecision } from "@hooks/common/session/replay-decision-utils";
import { buildScenarioRetryPayload } from "@hooks/game/activity/session/scenario-retry.utils";
import { getSessionFeedbackReplayResultId } from "@hooks/game/activity/session/session-results.utils";
import useI18n from "@i18n";
import { Alert, Button, Stack, ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material";
import { useContext } from "react";

interface IScenarioRetry {
  variant?: "action" | "comparison";
  originalDecisions?: ReplayDecision[];
}

const ScenarioRetry = ({ variant = "action", originalDecisions = [] }: IScenarioRetry) => {
  const { state } = useContext(AppContext);
  const { sessionResults } = useSessionResultsStaticContext();
  const { currentTab } = useSessionResultsTabContext();
  const retry = useScenarioRetryContext();
  const { i18n } = useI18n();
  const theme = useTheme();

  if (currentTab <= 0 || sessionResults?.game_mode !== GameModes.SCENARIO) return null;

  const resultId = getSessionFeedbackReplayResultId(sessionResults, currentTab);
  const playItem = { ...sessionResults.play_items[currentTab - 1], cognitive_result_id: resultId };
  const hasComparison = Boolean(retry.results[resultId]);
  const showingRetry = Boolean(hasComparison && retry.views[resultId]);
  if (variant === "comparison" && !hasComparison) return null;
  const canLaunch = Boolean(
    resultId && buildScenarioRetryPayload(playItem) && state.device.isUnityEmbedded && window.vuplex?.postMessage && !window.vuplexMock
  );
  const isBusy = retry.activeResultId !== null;
  const phaseLabels = {
    launching: "session-results.retry.launching",
    playing: "session-results.retry.waiting",
    evaluating: "session-results.retry.evaluating",
  };
  const labelKey = retry.activeResultId === resultId && retry.phase ? phaseLabels[retry.phase] : "session-results.retry.action";

  return (
    <Stack position="relative" direction="row" gap={1} alignItems="center">
      {variant === "comparison" && (
        <ToggleButtonGroup
          exclusive
          value={showingRetry ? "latest" : "original"}
          onChange={(_, value: "original" | "latest" | null) => {
            if (value !== null) retry.setShowRetry(resultId, value === "latest");
          }}
          data-shape="pill"
          aria-label={i18n("session-results.retry.compare")}
        >
          <ToggleButton value="original">{i18n("session-results.retry.original")}</ToggleButton>
          <ToggleButton value="latest">{i18n("session-results.retry.latest")}</ToggleButton>
        </ToggleButtonGroup>
      )}
      {variant === "action" && (
        <BybConditionalWrapper
          condition={hasBetterReplayDecision(retry.results[resultId]?.decisions ?? originalDecisions)}
          trueWrapper={children => (
            <BybAnimatedBorder
              borderRadius={theme.spacing(20)}
              borderWidth={2}
              color={theme.palette.dsYellow?.v300 ?? theme.palette.dsWarning.v300}
              sx={{ "@media (prefers-reduced-motion: reduce)": { "& *::before": { animation: "none" } } }}
            >
              {children}
            </BybAnimatedBorder>
          )}
        >
          <HapticsButtonWrapper disabled={isBusy || !canLaunch}>
            <Button
              disabled={isBusy || !canLaunch}
              onClick={() => retry.launch(playItem, originalDecisions)}
              startIcon={<Icon icon={faRotateRight} size="sm" color="inherit" />}
              data-shape="pill"
            >
              {i18n(labelKey)}
            </Button>
          </HapticsButtonWrapper>
        </BybConditionalWrapper>
      )}
      {variant === "action" && retry.errorResultId === resultId && (
        <Alert severity="warning" sx={{ position: "absolute", top: "calc(100% + 8px)", right: 0, width: 280, maxWidth: "70vw" }}>
          {i18n("session-results.retry.error")}
        </Alert>
      )}
    </Stack>
  );
};

export default ScenarioRetry;
